import React, { useState } from 'react';
import styled from 'styled-components';
import { statsApi, formatCurrency } from '../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;

  h2 {
    margin-top: 0;
    color: #333;
    font-size: 1.2rem;
  }
`;

const EntryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #eee;
  border-radius: 6px;
  margin-bottom: 10px;
  background: #fafafa;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 8px;

  input {
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    font-family: inherit;
  }
`;

const AmountInput = styled.input`
  width: 140px;
`;

const MemoInput = styled.input`
  flex: 1;
`;

const RowButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SmallButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;

  &.save {
    background-color: #007bff;
    color: white;
  }

  &.delete {
    background-color: #f44336;
    color: white;
  }

  &.add {
    background-color: #2e7d32;
    color: white;
  }
`;

const EmptyState = styled.p`
  color: #888;
  font-size: 0.9rem;
  margin: 10px 0 20px;
`;

const CloseButton = styled.button`
  margin-top: 10px;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 4px;
  background-color: #6c757d;
  color: white;
  font-size: 15px;
  cursor: pointer;
`;

function BonusEntryRow({ entry, month, person, onChanged }) {
  const [amount, setAmount] = useState(entry.amount);
  const [memo, setMemo] = useState(entry.memo || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!amount || amount <= 0) {
      alert('금액을 올바르게 입력해주세요.');
      return;
    }
    setSaving(true);
    try {
      await statsApi.updateSalary(entry.index, amount, month, person, '보너스', memo);
      onChanged();
    } catch (error) {
      console.error('Error updating bonus:', error);
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : '보너스 수정 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('이 보너스 항목을 삭제할까요?')) return;
    setSaving(true);
    try {
      await statsApi.deleteSalary(entry.index);
      onChanged();
    } catch (error) {
      console.error('Error deleting bonus:', error);
      alert('보너스 삭제 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntryRow>
      <FieldRow>
        <AmountInput
          type="number"
          min="0"
          value={amount}
          onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
        />
        <MemoInput
          type="text"
          placeholder="메모 (예: 2월 PS)"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />
      </FieldRow>
      <RowButtons>
        <SmallButton className="delete" onClick={handleDelete} disabled={saving}>삭제</SmallButton>
        <SmallButton className="save" onClick={handleSave} disabled={saving}>저장</SmallButton>
      </RowButtons>
    </EntryRow>
  );
}

function NewBonusRow({ month, person, onChanged }) {
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    const numericAmount = parseFloat(amount) || 0;
    if (numericAmount <= 0) {
      alert('금액을 올바르게 입력해주세요.');
      return;
    }
    setSaving(true);
    try {
      await statsApi.addSalary(numericAmount, month, person, '보너스', memo);
      setAmount('');
      setMemo('');
      onChanged();
    } catch (error) {
      console.error('Error adding bonus:', error);
      const detail = error.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : '보너스 추가 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntryRow style={{ background: '#f0f7ff', borderStyle: 'dashed' }}>
      <FieldRow>
        <AmountInput
          type="number"
          min="0"
          placeholder="금액"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <MemoInput
          type="text"
          placeholder="메모 (예: 하반기 PI)"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />
      </FieldRow>
      <RowButtons>
        <SmallButton className="add" onClick={handleAdd} disabled={saving}>보너스 추가</SmallButton>
      </RowButtons>
    </EntryRow>
  );
}

function BonusListModal({ isOpen, onClose, month, person, entries, onChanged }) {
  if (!isOpen) return null;

  const total = entries.reduce((sum, e) => sum + e.amount, 0);

  // 바깥을 눌러도 닫지 않는다. 적던 내용이 통째로 날아가는 것보다
  // 닫는 버튼을 한 번 더 누르는 편이 낫다.
  return (
    <ModalOverlay>
      <ModalContent>
        <h2>{month} {person} 보너스 내역</h2>
        {entries.length > 0 && (
          <p style={{ color: '#555', marginTop: '-8px' }}>합계: {formatCurrency(total)} ({entries.length}건)</p>
        )}

        {entries.length === 0 ? (
          <EmptyState>등록된 보너스가 없습니다. 아래에서 추가해주세요.</EmptyState>
        ) : (
          entries.map((entry) => (
            <BonusEntryRow
              key={entry.index}
              entry={entry}
              month={month}
              person={person}
              onChanged={onChanged}
            />
          ))
        )}

        <NewBonusRow month={month} person={person} onChanged={onChanged} />

        <CloseButton onClick={onClose}>닫기</CloseButton>
      </ModalContent>
    </ModalOverlay>
  );
}

export default BonusListModal;
